import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { idZodSchema } from "@@/shared/id";
import { phoneNumberZodSchema } from "@@/shared/phone";
import { generateId } from "@@/utilities/generate-id";
import type { Except } from "type-fest";
import { z } from "zod";

// COMMAND
type PhoneSignInVerifyCommand = {
    readonly _id: string;
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly _type: "PHONE_SIGN_IN_VERIFY_COMMAND";
    readonly phoneNumber: string;
    readonly verificationCode: number;
};

type CommandParams = Except<
    PhoneSignInVerifyCommand,
    "_id" | "_type" | "_createdAt"
>;
const phoneSignInVerifyCommandFactory = (params: CommandParams) => {
    const phoneSignInVerifyCommand: PhoneSignInVerifyCommand = {
        ...params,
        _id: generateId({ prefix: "phone_sign_in_verify_command__" }),
        _type: "PHONE_SIGN_IN_VERIFY_COMMAND",
        _createdAt: nowIsoAndUnix(),
    };
    return phoneSignInVerifyCommand;
};

const phoneSignInVerifyCommandZodSchema = z.object({
    _id: idZodSchema,
    _type: z.literal("PHONE_SIGN_IN_VERIFY_COMMAND"),
    _createdAt: isoAndUnixZodSchema,
    phoneNumber: phoneNumberZodSchema,
    verificationCode: z.number(),
});

// COMMAND RESPONSE
type PhoneSignInVerifyCommandResponse = {
    readonly _id: string;
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly _type: "PHONE_SIGN_IN_VERIFY_COMMAND_RESPONSE";
    readonly mode: "SIGN_IN" | "SIGN_UP";
};

type CommandResponseParams = Except<
    PhoneSignInVerifyCommandResponse,
    "_id" | "_type" | "_createdAt"
>;
const phoneSignInVerifyCommandResponseFactory = (
    params: CommandResponseParams,
) => {
    const phoneSignInVerifyCommandResponse: PhoneSignInVerifyCommandResponse = {
        ...params,
        _id: generateId({ prefix: "phone_sign_in_verify_command_response__" }),
        _type: "PHONE_SIGN_IN_VERIFY_COMMAND_RESPONSE",
        _createdAt: nowIsoAndUnix(),
    };
    return phoneSignInVerifyCommandResponse;
};

const phoneSignInVerifyCommandResponseZodSchema = z.object({
    _id: idZodSchema,
    _type: z.literal("PHONE_SIGN_IN_VERIFY_COMMAND_RESPONSE"),
    _createdAt: isoAndUnixZodSchema,
    mode: z.union([z.literal("SIGN_IN"), z.literal("SIGN_UP")]),
});

export {
    phoneSignInVerifyCommandFactory,
    // COMMAND RESPONSE
    phoneSignInVerifyCommandResponseFactory,
    phoneSignInVerifyCommandResponseZodSchema,
    phoneSignInVerifyCommandZodSchema,
};
export type {
    CommandParams as CreatePhoneSignInVerifyCommandParams,
    CommandResponseParams as CreatePhoneSignInVerifyCommandResponseParams,
    PhoneSignInVerifyCommand,
    PhoneSignInVerifyCommandResponse,
};
